import { MAX_NOTE_LENGTH } from 'Config';

const ELLIPSIS = '...';
const SECTION_SEPARATOR = '\n';
const NEWLINE_EFFECTIVE_COST = 2;

const sanitizeNotePart = (noteText: string): string =>
  noteText.replace(/[\r\n]+/g, ' ');

export const getEffectiveNoteLength = (note: string): number =>
  note.length + (note.match(/\n/g)?.length ?? 0);

const buildNoteWithoutOriginal = (
  header: string,
  sanitizedUserNote: string,
): string => `${header}${SECTION_SEPARATOR}${sanitizedUserNote}`;

const buildNoteWithOriginal = (
  header: string,
  sanitizedOriginal: string,
  sanitizedUserNote: string,
): string =>
  [header, sanitizedOriginal, sanitizedUserNote].join(SECTION_SEPARATOR);

export const buildDepartmentInitiatedNote = (
  header: string,
  userNote: string,
  originalInquiryContent?: string,
  maxLength = MAX_NOTE_LENGTH,
): string => {
  const sanitizedUserNote = sanitizeNotePart(userNote);
  const sanitizedOriginal = originalInquiryContent
    ? sanitizeNotePart(originalInquiryContent.trim())
    : '';

  const noteWithoutOriginal = buildNoteWithoutOriginal(
    header,
    sanitizedUserNote,
  );

  if (!sanitizedOriginal) {
    return noteWithoutOriginal;
  }

  const availableForOriginal =
    maxLength -
    header.length -
    sanitizedUserNote.length -
    NEWLINE_EFFECTIVE_COST * 2;

  if (availableForOriginal <= 0) {
    return noteWithoutOriginal;
  }

  if (sanitizedOriginal.length <= availableForOriginal) {
    const noteWithFullOriginal = buildNoteWithOriginal(
      header,
      sanitizedOriginal,
      sanitizedUserNote,
    );

    if (getEffectiveNoteLength(noteWithFullOriginal) <= maxLength) {
      return noteWithFullOriginal;
    }
  }

  if (availableForOriginal <= ELLIPSIS.length) {
    return noteWithoutOriginal;
  }

  const truncatedOriginal =
    sanitizedOriginal.slice(0, availableForOriginal - ELLIPSIS.length) +
    ELLIPSIS;

  const noteWithTruncatedOriginal = buildNoteWithOriginal(
    header,
    truncatedOriginal,
    sanitizedUserNote,
  );

  if (getEffectiveNoteLength(noteWithTruncatedOriginal) <= maxLength) {
    return noteWithTruncatedOriginal;
  }

  return noteWithoutOriginal;
};
